import React, { useEffect, useState } from 'react';
import { useNotificationContext } from '@context/notification_context';
import { Bell, X } from 'lucide-react';
import { createPortal } from 'react-dom';

const NotificationsList: React.FC = () => {
    const { notifications, clearNotifications, dismissNotification } = useNotificationContext();
    const [isOpen, setIsOpen] = useState(false);
    const [toasts, setToasts] = useState<{ id: string, content: React.ReactNode, timer: NodeJS.Timeout | null }[]>([]);

    // Process notifications for toast display and browser notifications
    useEffect(() => {
        // Handle new notifications
        notifications.forEach(notification => {
            if (!notification.read) {
                // Check if already in toast list
                const existingToastIndex = toasts.findIndex(toast => toast.id === notification.id);

                if (existingToastIndex === -1) {
                    // Create browser notification for interview processing
                    if (notification.notification_type === 'GENERATED_INTERVIEW_INSIGHT' && notification.data.content) {
                        if (Notification && Notification.permission === 'granted') {
                            new Notification('Interview Processing Complete', {
                                body: notification.data.content,
                                icon: `${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/ourteam_logo_dashboard.png`
                            });
                        } else if (Notification && Notification.permission !== 'denied') {
                            Notification.requestPermission();
                        }
                    }

                    // Create toast notification
                    const content = (
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 mb-3 border border-gray-200 dark:border-gray-700 max-w-sm w-full animate-slideIn">
                            <div className="flex justify-between items-start">
                                <div>
                                    <h3 className="font-medium text-gray-900 dark:text-white">
                                        {notification.data.title}
                                    </h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                                        {notification.data.content}
                                    </p>
                                    {/* {notification.data.interview_status && (
                                        <p className="text-xs mt-1 text-gray-500">
                                            Status: {notification.data.interview_status}
                                        </p>
                                    )} */}
                                    <p className="text-xs text-gray-400 mt-1">
                                        {new Date(notification.timestamp).toLocaleTimeString()}
                                    </p>
                                </div>
                                <button
                                    onClick={() => dismissNotification(notification.id)}
                                    className="text-gray-400 hover:text-gray-600"
                                    aria-label="Close"
                                >
                                    <X size={16} />
                                </button>
                            </div>
                        </div>
                    );

                    // Auto-dismiss after 5 seconds
                    const timer = setTimeout(() => {
                        dismissNotification(notification.id);
                    }, 50000);

                    setToasts(prev => [...prev, { id: notification.id, content, timer }]);
                }
            }
        });

        // Clean up toasts when notifications are dismissed
        setToasts(prev => prev.filter(
            toast => notifications.some(notification => notification.id === toast.id)
        ));

    }, [notifications, dismissNotification]);

    // Clean up timers when component unmounts
    useEffect(() => {
        return () => {
            toasts.forEach(toast => {
                if (toast.timer) clearTimeout(toast.timer);
            });
        };
    }, [toasts]);

    // Render the notification badge and dropdown
    return (
        <>
            {/* Toast notifications portal
            {createPortal(
                <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 items-end">
                    {toasts.map((toast) => (
                        <div key={toast.id}>{toast.content}</div>
                    ))}
                </div>,
                document.body
            )} */}

            {/* Notification icon and dropdown - hide if empty */}
            {notifications.length > 0 && (
                <div className="fixed bottom-4 right-4 z-40">
                    <button
                        onClick={() => setIsOpen(!isOpen)}
                        className="bg-blue-500 hover:bg-blue-600 text-white rounded-full p-2 shadow-lg relative"
                    >
                        <Bell size={24} />
                        {notifications.some(n => !n.read) && (
                            <span className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                                {notifications.filter(n => !n.read).length}
                            </span>
                        )}
                    </button>

                    {isOpen && (
                        <div className="absolute bottom-14 right-0 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                            <div className="p-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                                <h3 className="font-medium">Notifications</h3>
                                <button
                                    onClick={clearNotifications}
                                    className="text-xs text-blue-500 hover:text-blue-700"
                                >
                                    Clear all
                                </button>
                            </div>
                            <div className="max-h-96 overflow-y-auto p-2">
                                {notifications.length > 0 ? (
                                    notifications.map((notification) => (
                                        <div
                                            key={notification.id}
                                            className={`p-3 mb-2 rounded-lg ${notification.read ? 'bg-gray-50 dark:bg-gray-900' : 'bg-blue-50 dark:bg-gray-800'}`}
                                        >
                                            <div className="flex justify-between">
                                                <h4 className="text-sm font-medium">
                                                    {notification.data.title.replace(/_/g, ' ')}
                                                </h4>
                                                <button
                                                    onClick={() => dismissNotification(notification.id)}
                                                    className="text-gray-400 hover:text-gray-600"
                                                >
                                                    <X size={14} />
                                                </button>
                                            </div>
                                            <p className="text-xs mt-1">
                                                {notification.data.content}
                                            </p>
                                            <div className="text-xs text-gray-500 mt-1">
                                                {new Date(notification.timestamp).toLocaleString()}
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="p-4 text-center text-gray-500">
                                        No notifications
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default NotificationsList;